/* eslint-disable react-hooks/exhaustive-deps */
import { APIProvider, Map, MapEvent } from '@vis.gl/react-google-maps';
import React, { useEffect, useRef, useState } from 'react';
import { Container } from 'react-bootstrap';
import { LatLng } from '../../../services/helper';
import { Course } from '../../../types/course.type';
import { course_type_marker_lookup } from '../../maps/components';
import { MarkerWithIcon } from '../../maps/components/MarkerWithIcon';

interface CourseMapProps {
  courses: Course[];
  location?: LatLng;
  selectedCourseId?: number;
  onVenueClick: (course: Course) => void;
}

const DEFAULT_CENTER: LatLng = { lat: 41.831909, lng: -87.698316 };

const getCenter = (location?: LatLng) => ({
  lat: location?.lat ?? DEFAULT_CENTER.lat,
  lng: location?.lng ?? DEFAULT_CENTER.lng
});

const CourseMap: React.FC<CourseMapProps> = ({
  courses,
  location,
  selectedCourseId,
  onVenueClick
}) => {
  const [center, setCenter] = useState<LatLng>(() => getCenter(location));
  const mapRef = useRef<any>(null);
  const initialCenterAppliedRef = useRef(false);
  const googleApiKey = process.env.REACT_APP_GOOGLE_API_KEY || '';

  useEffect(() => {
    const newCenter = getCenter(location);
    setCenter(newCenter);
    // Recenter only when the search location actually changes — not on every
    // tile load, which fired on each pan/zoom and snapped the map back.
    mapRef.current?.panTo(newCenter);
  }, [location?.lat, location?.lng]);

  // Capture the map instance once it's ready. onTilesLoaded fires after every
  // pan/zoom, so only apply the initial center here (before mapRef existed).
  const handleMapLoad = (event: MapEvent<unknown>) => {
    mapRef.current = event.map;
    if (!initialCenterAppliedRef.current && location) {
      mapRef.current.panTo(getCenter(location));
      initialCenterAppliedRef.current = true;
    }
  };

  return (
    <div className="map-container">
      <Container>
        <div className="course-map-frame">
          <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0 }}>
            <APIProvider apiKey={googleApiKey}>
              <Map
                defaultCenter={center}
                defaultZoom={10}
                mapId={'784a0a7ed39c3fd8'}
                disableDefaultUI
                fullscreenControl={false}
                zoomControl={false}
                streetViewControl={false}
                mapTypeControl={false}
                onTilesLoaded={handleMapLoad}
              >
                {courses.map((c) => {
                  if (!(c.venue.latitude && c.venue.longitude)) {
                    return <div key={c.id}></div>;
                  }
                  return (
                    <MarkerWithIcon
                      key={c.id}
                      onClick={() => onVenueClick(c)}
                      icon={course_type_marker_lookup(c.course_type, selectedCourseId === c.id)}
                      position={{ lat: c.venue.latitude, lng: c.venue.longitude }}
                    />
                  );
                })}
              </Map>
            </APIProvider>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default CourseMap;
